import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect, useRef } from 'react';
import { SCHEDULING_PREFERENCES_API_SERVICES } from '../api';
import { useShowToast } from '@/hooks';

interface PollResponse {
  data: {
    status: 'PENDING' | 'SUCCESS' | 'FAILURE';
    results?: any;
  };
}

export const useSharedCalendarPolling = ({
  type,
  autoFetch = true,
}: {
  type: string;
  autoFetch?: boolean;
}) => {
  const toast = useShowToast();
  const MAX_RETRIES = 20;
  const POLL_INTERVAL = 2000;

  const [isTriggered, setIsTriggered] = useState(false);
  const [isMaxRetriesExceeded, setIsMaxRetriesExceeded] = useState(false);

  const toastFlags = useRef({
    maxRetriesToastShown: false,
    failureToastShown: false,
  });

  const pollCount = useRef(0);

  // Auto-trigger fetch if autoFetch is enabled. The set-state-in-effect
  // rule is intentionally suppressed: we need to react to runtime changes
  // of `autoFetch` (a prop that can flip false → true once the calendar
  // connection is detected), so a derived initial-state isn't sufficient.
  useEffect(() => {
    if (autoFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsTriggered(true);
    }
  }, [autoFetch]);
  // Reset max retries state when triggering new fetch
  const resetStates = () => {
    setIsMaxRetriesExceeded(false);
    toastFlags.current.maxRetriesToastShown = false;
    toastFlags.current.failureToastShown = false;
    pollCount.current = 0;
  };

  // Step 1: Get the UUID - only when manually triggered
  const {
    data: uuidData,
    isPending: isUuidLoading,
    refetch: refetchUuid,
  } = useQuery({
    queryKey: ['sharedCalendarUUID', type],
    queryFn: async () => {
      resetStates(); // Reset states on new fetch
      return await SCHEDULING_PREFERENCES_API_SERVICES.getSharedCalendarUUID();
    },
    gcTime: 0, // Don't cache this query
    enabled: isTriggered, // Only fetch when manually triggered
    staleTime: 0,
  });

  // Step 2: Fetch  data using the UUID
  const { data: pollResponse, isPending: isPollLoading } =
    useQuery<PollResponse>({
      queryKey: [
        'sharedCalendarsPollResponse',
        type,
        uuidData?.data?.fetch_uuid,
      ],
      queryFn: async () => {
        if (!uuidData?.data?.fetch_uuid) {
          throw new Error('No UUID available');
        }

        return await SCHEDULING_PREFERENCES_API_SERVICES.getCalendarsPollResponse(
          {
            uuId: uuidData.data.fetch_uuid,
            type,
          }
        );
      },
      gcTime: 0,
      enabled: isTriggered && !!uuidData?.data?.fetch_uuid, // Only when triggered AND UUID is available
      staleTime: 0,
      refetchInterval: (queryState) => {
        // Only poll if status is PENDING. queryState can be undefined on
        // first run, so fall back to an empty object before destructuring
        // — bare `?.state` would short-circuit and break the destructure.
        const { data: apiResponse } = queryState?.state ?? {};

        if (apiResponse?.data?.status === 'FAILURE') {
          if (!toastFlags.current.failureToastShown) {
            toast({
              title:
                'Unable to load the available shared calendars. Try again later.',
              status: 'error',
            });
            toastFlags.current.failureToastShown = true;
          }
          return false;
        }
        if (apiResponse?.data?.status === 'PENDING') {
          pollCount.current += 1;
          if (pollCount.current > MAX_RETRIES) {
            if (!toastFlags.current.maxRetriesToastShown) {
              toast({
                title:
                  'Unable to load the available shared calendars. Try again later.',
                status: 'error',
              });
              toastFlags.current.maxRetriesToastShown = true;
            }
            setIsMaxRetriesExceeded(true);
            return false;
          }

          return POLL_INTERVAL;
        }

        return false;
      },
      retry: 20,
    });

  const isLoading = useMemo(() => {
    if (!isTriggered) return false;
    return (
      isUuidLoading || isPollLoading || pollResponse?.data?.status === 'PENDING'
    );
  }, [isTriggered, isUuidLoading, isPollLoading, pollResponse]);

  const sharedCalendarOptions = useMemo(
    () =>
      pollResponse?.data?.status === 'SUCCESS'
        ? pollResponse?.data?.results
        : undefined,
    [pollResponse]
  );

  // Manual trigger function that only starts fetching when called
  const triggerFetch = () => {
    setIsTriggered(true);
    return refetchUuid();
  };

  return {
    isLoading,
    sharedCalendarOptions,
    refetchUuid: triggerFetch,
    isMaxRetriesExceeded,
    fetchUuid: uuidData?.data?.fetch_uuid,
    queryKey: ['sharedCalendarsPollResponse', type, uuidData?.data?.fetch_uuid],
  };
};
